import { atom } from 'jotai'
import { atomFamily } from 'jotai-family'
import { atomWithStorage } from 'jotai/utils'

import { STORAGE_KEYS } from '@/lib/configs/storage-config'
import { DEFAULT_POSITION_GROUP_STATE, type PositionGroupState } from '@/types/position-group'

import { safeStorage } from './storage'

/**
 * Position group state atom — persisted to localStorage.
 *
 * Stores UI-only state: mode, collapse flags, isRenamed flags.
 * group.id values are server-assigned groupIds.
 * The hook `usePositionGroups` wraps this with business logic.
 */
export const positionGroupStateAtom = atomWithStorage<PositionGroupState>(
  STORAGE_KEYS.dashboard.positionGroups,
  DEFAULT_POSITION_GROUP_STATE,
  safeStorage,
  { getOnInit: true }
)

/** Whether the "Total" summary header row is collapsed in none-mode. */
export const totalRowCollapsedAtom = atom(false)

/**
 * Whether the "Spot" summary group (none-mode only — spot/cash balance rows
 * pinned at the bottom of the table) is collapsed, per widget instance so
 * multiple Position Table cards on the same dashboard don't share one state.
 */
export const spotRowCollapsedAtomFamily = atomFamily((_cardId: string) => atom(false))

/**
 * "Aggregate positions" view — one row per holding instead of one per account
 * (see features/dashboard/lib/position-aggregation.ts). Global, like the group
 * mode above, so every Positions widget and the option chain agree.
 */
export const aggregatePositionsAtom = atomWithStorage<boolean>(
  STORAGE_KEYS.dashboard.aggregatePositions,
  false,
  safeStorage,
  { getOnInit: true }
)

/** Aggregate rows currently expanded into their per-account rows (collapsed by default). */
export const expandedAggregateKeysAtom = atom<ReadonlySet<string>>(new Set<string>())
